// src/auth.js
const TOKEN_KEY = 'neoteeforge_auth_token';
const USER_KEY = 'neoteeforge_auth_user';
const LEGACY_AUTH_KEY = 'neotee_auth';

// Store authentication token
export const setAuthToken = (token, user) => {
  try {
    console.log('💾 Saving auth data:', { 
      tokenPresent: !!token, 
      user: user ? { id: user.id, email: user.email, role: user.role } : null 
    });
    
    if (!token) {
      console.error('❌ Cannot save auth: Token is empty');
      return false;
    }
    
    if (!user) {
      console.error('❌ Cannot save auth: User data is empty');
      return false;
    }
    
    // Save in new format
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    
    // Also keep legacy format for compatibility
    localStorage.setItem(LEGACY_AUTH_KEY, JSON.stringify({ token, user }));
    
    console.log('✅ Auth data saved successfully');
    return true;
  } catch (error) {
    console.error('❌ Error storing auth data:', error);
    return false;
  }
};

// Get authentication token
export const getAuthToken = () => {
  try {
    // First try new format
    let token = localStorage.getItem(TOKEN_KEY);
    
    // Fallback to legacy format
    if (!token) {
      const legacyAuth = localStorage.getItem(LEGACY_AUTH_KEY);
      if (legacyAuth) {
        try {
          const authData = JSON.parse(legacyAuth);
          token = authData.token;
          // Migrate to new format
          if (token && authData.user) {
            console.log('🔄 Migrating legacy auth format to new format');
            setAuthToken(token, authData.user);
          }
        } catch (parseError) {
          console.error('❌ Error parsing legacy auth data:', parseError);
        }
      }
    }
    
    console.log('🔐 Token retrieved:', !!token);
    return token;
  } catch (error) {
    console.error('❌ Error retrieving auth token:', error);
    return null;
  }
};

// Get user data
export const getUserData = () => {
  try {
    // First try new format
    let userStr = localStorage.getItem(USER_KEY);
    let user = userStr ? JSON.parse(userStr) : null;
    
    // Fallback to legacy format
    if (!user) {
      const legacyAuth = localStorage.getItem(LEGACY_AUTH_KEY);
      if (legacyAuth) {
        try {
          const authData = JSON.parse(legacyAuth);
          user = authData.user;
          // Migrate to new format
          if (authData.token && user) {
            console.log('🔄 Migrating legacy user data to new format');
            setAuthToken(authData.token, user);
          }
        } catch (parseError) {
          console.error('❌ Error parsing legacy user data:', parseError);
        }
      }
    }
    
    console.log('👤 User data retrieved:', user ? { id: user.id, email: user.email, role: user.role } : null);
    return user;
  } catch (error) {
    console.error('❌ Error retrieving user data:', error);
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  const user = getUserData();
  
  const authenticated = !!(token && user);
  
  console.log('🔐 Authentication check:', {
    hasToken: !!token,
    hasUser: !!user,
    userRole: user?.role,
    authenticated: authenticated
  });
  
  return authenticated;
};

// Check if user is admin - FIXED VERSION
export const isAdmin = () => {
  const user = getUserData();
  
  if (!user) {
    console.log('👑 Admin check: No user data');
    return false;
  }
  
  // Check both email and role for admin
  const isAdminUser = user.role === 'admin' || user.email === 'fitrdeen@gmail.com';
  
  console.log('👑 Admin status check:', {
    user: user ? { email: user.email, role: user.role } : null,
    isAdmin: isAdminUser
  });
  
  return isAdminUser;
};

// Clear authentication data
export const clearAuth = () => {
  try {
    console.log('🗑️ Clearing auth data');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LEGACY_AUTH_KEY);
    console.log('✅ Auth data cleared successfully');
  } catch (error) {
    console.error('❌ Error clearing auth data:', error);
  }
};

// SIMPLIFIED token validation - only check if token exists
export const validateToken = (token) => {
  if (!token || token === 'undefined' || token === 'null') {
    console.error('❌ Token is empty or invalid');
    return false;
  }
  
  console.log('✅ Token validation passed (basic check)');
  return true;
};

// Initialize auth state on app load
export const initializeAuth = () => {
  const token = getAuthToken();
  const user = getUserData();
  
  // Validate token
  if (token && !validateToken(token)) {
    console.warn('⚠️ Invalid token detected, clearing auth');
    clearAuth();
    return { token: null, user: null, authenticated: false, isAdmin: false };
  }
  
  const authenticated = !!(token && user);
  const admin = isAdmin();
  
  console.log('🚀 Auth initialization:', {
    tokenPresent: !!token,
    userPresent: !!user,
    authenticated: authenticated,
    isAdmin: admin,
    user: user ? { id: user.id, email: user.email, role: user.role } : null
  });
  
  return { token, user, authenticated, isAdmin: admin };
};

// Check and refresh auth state
export const checkAuthState = () => {
  return initializeAuth();
};

// Legacy functions for backward compatibility
export function saveAuth(data) {
  if (data && data.token && data.user) {
    return setAuthToken(data.token, data.user);
  } else {
    console.error('❌ saveAuth: Invalid data format', data);
    return false;
  }
}

export function loadAuth() {
  const token = getAuthToken();
  const user = getUserData();
  
  if (token && user) {
    return { token, user };
  }
  
  return null;
}

export function getCurrentUser() {
  return getUserData();
}

// Export everything
export default {
  setAuthToken,
  getAuthToken,
  getUserData,
  isAuthenticated,
  isAdmin,
  clearAuth,
  validateToken,
  initializeAuth,
  checkAuthState,
  saveAuth,
  loadAuth,
  getCurrentUser
};